import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup
} from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { PageComponent } from "@components/page/page.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SearchComponent } from "@components/search/search.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { TranslatePipe } from "@ngx-translate/core";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { StatedataService, Volatile } from "~app/common/services/statedata.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { CkleverantorListComponent } from "./components/ckleverantor-list/ckleverantor-list.component";
import { ICKLeverantorList } from "./models/ckLeverantorList";
import { CKLeverantorSearchParamsModel } from "./models/ckLeverantorSearchParamsModel";
import { CKLeverantorService } from "./services/ckleverantor.service";

@Component({
    selector: "rask-ckleverantor",
    templateUrl: "./ckleverantor.component.html",
    styleUrls: ["./ckleverantor.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        FormsModule,
        ReactiveFormsModule,
        SearchComponent,
        RadiobuttonGroupComponent,
        TogglePanelComponent,
        InputComponent,
        SubmitButtonComponent,
        ButtonComponent,
        CkleverantorListComponent,
        TranslatePipe
    ]
})
export class CkleverantorComponent implements OnInit {
    aktivButtons = [
        { key: true,
            value: "Ja" },
        { key: false,
            value: "Nej" },
        { key: null,
            value: "Alla" }
    ] as IRadiobuttonItem[];
    ckLeverantorSearchParams: CKLeverantorSearchParamsModel;
    ckLeverantorer: ICKLeverantorList[];
    form: UntypedFormGroup;
    volatile: Volatile;

    private readonly busyService = inject(BusyService);
    private readonly ckLeverantorService = inject(CKLeverantorService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly stateDataService = inject(StatedataService);
    private readonly webinstallningarService = inject(WebinstallningarService);
    private readonly wik = "CKLeverantor.searchparams";

    constructor() {
        this.volatile = this.stateDataService.getVolatile();
    }

    mapFromForm(): CKLeverantorSearchParamsModel {
        return {
            aktiv: this.form.get("aktiv").value,
            sokTypeAhead: this.form.get("sokTypeAhead").value,
            id: null,
            namn: this.form.get("namn").value,
            orgNummer: this.form.get("orgNummer").value,
            besokPostortadress: this.form.get("besokPostortadress").value,
            huvudkod: this.form.get("huvudkod").value,
            internnummer: this.form.get("internnummer").value,
            limeId: this.form.get("limeId").value
        };
    }

    ngOnInit() {
        this.ckLeverantorSearchParams = this.volatile.get<CKLeverantorSearchParamsModel>(this.wik);
        if (this.ckLeverantorSearchParams) {
            this.form = this.formBuilder.group(this.ckLeverantorSearchParams);
            this.form.addControl("active", new UntypedFormControl(this.parseFalsyBool(this.ckLeverantorSearchParams.aktiv)));
            this.search(false);
            return;
        }

        this.resetToWebinstallning();
    }

    reset() {
        this.form.get("sokTypeAhead").setValue(null);
        this.form.get("aktiv").setValue(null);
        this.form.get("id").setValue(null);
        this.form.get("namn").setValue(null);
        this.form.get("orgNummer").setValue(null);
        this.form.get("besokPostortadress").setValue(null);
        this.form.get("huvudkod").setValue(null);
        this.form.get("internnummer").setValue(null);
        this.form.get("limeId").setValue(null);
        this.ckLeverantorer = null;
    }

    resetToWebinstallning() {
        let searchParams = this.webinstallningarService.get<CKLeverantorSearchParamsModel>(this.wik);
        if (searchParams == null) {
            searchParams =
            {
                id: undefined,
                sokTypeAhead: "",
                namn: "",
                orgNummer: "",
                besokPostortadress: "",
                huvudkod: "",
                internnummer: "",
                limeId: "",
                aktiv: true
            };
        }

        this.ckLeverantorSearchParams = searchParams;
        this.form = this.formBuilder.group(this.ckLeverantorSearchParams);
        this.form.addControl("active", new UntypedFormControl(this.parseFalsyBool(searchParams.aktiv)));
    }

    search(saveState = true) {
        const params = this.mapFromForm();
        const query = this.ckLeverantorService.searchCKLeverantor(params);
        this.busyService.addWork(query).subscribe((ckleverantorer: ICKLeverantorList[]) => {
            this.ckLeverantorer = ckleverantorer;
        });

        if (saveState) {
            this.webinstallningarService.set<CKLeverantorSearchParamsModel>(this.wik, params);
            this.volatile.set(this.wik, params);
        }
    }

    searchCKLeverantorTypeAhead = (searchString: string) => this.ckLeverantorService.searchCKLeverantorTypeAhead(searchString);

    private parseFalsyBool(val: any) {
        if (val == null) {
            return "";
        }
        return val ? "Ja" : "Nej";
    }
}
